import { dateUtils } from '@festina-life/core'
import { useMemo } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import type { TemplateVersionInfo } from '../../types/templateModel'
import { useTemplateHierarchy } from '../../uiModels/versionInfo/useTemplateHierarchy'
import { getVersionDateBoundaries } from '../versionedTimeline/helpers'

/**
 * Utility hook used for retrieving metadata about the current templates.
 */
export const useTemplatesMetadata = (
  forTemplate: TemplateVersionInfo,
  allVersions: TemplateVersionInfo[],
) => {
  const { viewAt } = useConfigurationContext()
  const hierarchy = useTemplateHierarchy({
    forTemplate,
    withReplacements: false,
  })
  const templateIdentifierLengths = hierarchy.map(
    (versionInfo) => versionInfo.identifier.length,
  )
  const longestTemplateIdentifierLength = Math.max(...templateIdentifierLengths)
  const longestTemplateIdentifierIndex = templateIdentifierLengths.indexOf(
    longestTemplateIdentifierLength,
  )
  const longestTemplateIdentifier = hierarchy[longestTemplateIdentifierIndex]
  const versionDateBoundaries = useMemo(
    () =>
      getVersionDateBoundaries({
        items: allVersions.filter(
          (element) =>
            !!element.fromDate &&
            hierarchy.some((vInfo) => vInfo.identifier === element.identifier),
        ),
        getFromDate: (element) => element.fromDate,
        defaultDate: dateUtils.dateAsServerStr(viewAt),
      }),
    [allVersions, hierarchy, viewAt],
  )

  return {
    longestIdentifier: longestTemplateIdentifier.identifier,
    earliestDefinedVersionDate: versionDateBoundaries.earliestDate,
    latestDefinedVersionDate: versionDateBoundaries.latestDate,
  }
}
